import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseReport = API_JSON & {
  name: string;
  identifier: string;
  description?: string;
};

const order = { 'order[id]': 'asc' } as const;
const groups = ['report-details'];

/**
 * Fetch reports
 * @param params can be used to customise/limit the reports retrieved
 * @returns an array of reports
 */
export const getReports: GetMany<BaseReport> = async (params) =>
  (await ASSIST_API.get('reports', { params: requestParams({ order, groups }, params) })).data;

/**
 * Fetch report (singular)
 * @param params can be used to customise the report retrieved
 * @returns a single report
 */
export const getReport: GetOne<BaseReport> = async (id, params) =>
  (await ASSIST_API.get(`reports/${id}`, { params })).data;

/**
 * Hook implementation to fetch reports
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns reports matching request
 */
export const useReports: AssistQuery.UseMany<BaseReport> = (params, options) =>
  useQueryAssist(['reports', params], () => getReports(params), {
    staleTime: STALE_MEDIUM,
    ...options,
    allowNoFilters: true
  });

/**
 * Given a reportId fetch and return the report
 *  - only runs once reportId is provided
 *  - fetches the one report
 *  - caches data
 * @param id reportId
 * @param params optional parameters to change requested data
 * @returns the matching report
 */
export const useReport: AssistQuery.UseOne<BaseReport> = (id, params, options) =>
  useQueryAssist(['reports', { id, ...params }], () => getReport(id, params), {
    staleTime: STALE_MEDIUM,
    ...options,
    enabled: !!id
  });

export const useReportByIdentifier: AssistQuery.UseOne<BaseReport> = (
  identifier,
  params,
  options
) => {
  const nonNullIdentifier = identifier ?? undefined;
  const paramsWithIdentifierFilter = { filters: { identifier: nonNullIdentifier }, ...params };

  return useQueryAssist(
    ['reports', { identifier, ...paramsWithIdentifierFilter }],
    () =>
      getReports(paramsWithIdentifierFilter).then((response) => {
        if (response.json.length === 1) return { ...response, json: response.json[0] };
        throw Error('Unable to find report by identifier.');
      }),
    {
      staleTime: STALE_MEDIUM,
      ...options,
      enabled: !!nonNullIdentifier
    }
  );
};
